import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, CheckCircle, Briefcase, Rocket, Users, Target, Star, MessageSquare, Calendar, FileText, Zap, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Chatbot from '../components/Chatbot';
import SEOHead from '../components/SEOHead';
import { scrollToSection } from '../utils/smoothScroll';
import { apiFetch } from '../lib/apiClient';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Custom hook for scroll animations
const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold, rootMargin: '0px 0px -50px 0px' }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return [ref, isVisible];
};

// Animated Section Component
const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollAnimation(0.1);
  
  return (
    <div 
      ref={ref}
      className={`transition-all duration-700 ease-out ${className} ${
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-12'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const fallbackServices = [
  {
    id: '1',
    title: 'Strategic Advisory',
    subtitle: 'Per CEO e Founder',
    description: 'Affiancamento strategico mensile per prendere decisioni di business basate su dati e visione di lungo termine. Divento il tuo partner strategico di fiducia.',
    price: 'Da €3.000/mese',
    features: [
      'Call strategiche settimanali',
      'Accesso diretto prioritario',
      'Review trimestrale obiettivi',
      'Supporto decisioni critiche',
      'Report mensile performance'
    ],
    icon: 'Target',
    popular: true
  },
  {
    id: '2',
    title: 'Piano Marketing Strategico',
    subtitle: 'Progetto completo',
    description: 'Sviluppo di una strategia di marketing completa e personalizzata: posizionamento, messaging, canali, budget e KPI. Il blueprint per dominare il tuo mercato.',
    price: 'Da €8.000',
    features: [
      'Analisi competitor approfondita',
      'Strategia go-to-market',
      'Piano operativo 12 mesi',
      'KPI e dashboard personalizzata',
      'Positioning statement'
    ],
    icon: 'Briefcase',
    popular: false
  },
  {
    id: '3',
    title: 'Acceleratore di Crescita',
    subtitle: 'Programma 90 giorni',
    description: 'Programma intensivo per aziende pronte a scalare. 90 giorni di lavoro fianco a fianco per implementare strategie di crescita aggressive e misurabili.',
    price: 'Da €15.000',
    features: [
      'Workshop settimanali',
      'Implementazione guidata',
      'Supporto team dedicato',
      'Ottimizzazione continua',
      'Scaling delle strategie vincenti'
    ],
    icon: 'Rocket',
    popular: false
  },
  {
    id: '4',
    title: 'Audit & Diagnosi',
    subtitle: 'Analisi una tantum',
    description: 'Analisi completa del tuo marketing attuale: cosa funziona, cosa no, e dove stai lasciando soldi sul tavolo. Report dettagliato con priorità di intervento.',
    price: 'Da €2.500',
    features: [
      'Audit completo canali',
      'Analisi conversion funnel',
      'Report con action plan',
      'Identificazione quick wins',
      'Prioritizzazione interventi'
    ],
    icon: 'Star',
    popular: false
  }
];

const iconMap = {
  Target: Target,
  Users: Users,
  Rocket: Rocket,
  Briefcase: Briefcase,
  Star: Star
};

const ServiziPage = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState(fallbackServices);
  const [isLoaded, setIsLoaded] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await apiFetch(`${API_URL}/api/services`);
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            setServices(data);
          }
        }
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };
    fetchServices();
  }, []);

  const scrollToContact = () => {
    scrollToSection('contact');
  };

  const processSteps = [
    { 
      step: '01', 
      title: 'Primo contatto', 
      desc: 'Compili il form e ti ricontatto entro 24h per una prima call conoscitiva gratuita.',
      icon: MessageSquare
    },
    { 
      step: '02', 
      title: 'Analisi', 
      desc: 'Approfondiamo il tuo business, obiettivi e sfide. Capisco se posso aiutarti davvero.',
      icon: FileText
    },
    { 
      step: '03', 
      title: 'Proposta', 
      desc: 'Ricevi una proposta personalizzata con obiettivi, timeline e investimento chiaro.',
      icon: Calendar
    },
    { 
      step: '04', 
      title: 'Lavoriamo', 
      desc: 'Iniziamo la collaborazione con un kick-off e roadmap definita. Risultati garantiti.',
      icon: Zap
    },
  ];

  const faqs = [
    { 
      q: 'Come faccio a sapere quale servizio fa per me?', 
      a: 'Nella prima call gratuita analizziamo insieme la tua situazione e ti consiglio il percorso più adatto. Non vendo nulla che non sia realmente utile per te.' 
    },
    { 
      q: 'Lavori con aziende di qualsiasi settore?', 
      a: 'Mi specializzo in B2B e servizi professionali, ma ho esperienza anche in e-commerce e retail. Se il tuo business ha un prodotto/servizio valido e vuole crescere, possiamo parlarne.' 
    },
    { 
      q: 'Quanto tempo ci vuole per vedere i risultati?', 
      a: 'Dipende dal punto di partenza, ma generalmente i primi risultati tangibili arrivano entro 90 giorni. I grandi cambiamenti richiedono 6-12 mesi di lavoro costante.' 
    },
    { 
      q: 'Offri garanzie sui risultati?', 
      a: 'Non posso garantire numeri specifici perché dipendono da molti fattori. Quello che garantisco è il mio impegno totale e una metodologia provata. L\'89% dei miei clienti supera gli obiettivi.' 
    },
  ];

  const idealClient = [
    { text: 'Fatturi almeno €100K e vuoi scalare', icon: Target },
    { text: 'Hai un prodotto/servizio valido ma il marketing non funziona', icon: Briefcase },
    { text: 'Sei stanco di agenzie che non capiscono il tuo business', icon: Users },
    { text: 'Vuoi risultati misurabili, non vanity metrics', icon: Star },
    { text: 'Cerchi un partner strategico, non un esecutore', icon: Rocket }
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEOHead page="servizi" />
      <Navbar />
      
      {/* Hero Section - Full Screen */}
      <section className="relative min-h-screen bg-[#0b114e] overflow-hidden">
        {/* Background Image - Extended on mobile to avoid edge line */}
        <div 
          className="absolute top-0 right-0 h-full w-full lg:w-[55%]"
          style={isMobile ? { left: '-20%', right: 'auto', width: '120%' } : {}}
        >
          <div 
            className="absolute inset-0 bg-cover bg-no-repeat"
            style={{
              backgroundImage: `url('/media/nicola-hero.webp')`,
              backgroundPosition: isMobile ? '-30% top' : 'center top',
            }}
          />
          <div className="absolute inset-0 bg-[#0b114e]/5" />
          {/* Gradient inside image container */}
          <div className={`absolute inset-0 ${
            isMobile 
              ? 'bg-gradient-to-r from-[#0b114e] from-0% via-[#0b114e]/80 via-40% to-transparent to-70%' 
              : 'bg-gradient-to-r from-[#0b114e] from-0% via-[#0b114e]/70 via-40% to-transparent to-80%'
          }`} />
        </div>

        {/* Content */}
        <div className="relative min-h-screen flex items-center">
          <div className="w-full max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="max-w-2xl">
              {/* Badge */}
              <div 
                className={`hidden sm:inline-flex items-center gap-2 bg-white/5 backdrop-blur-sm rounded-full px-4 py-2 mb-8 border border-white/10 transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: '200ms' }}
              >
                <span className="w-2 h-2 bg-[#d4883a] rounded-full animate-pulse" />
                <span className="text-white/70 text-sm tracking-widest">I miei servizi</span>
              </div>

              {/* Main Heading */}
              <h1 
                className={`text-3xl sm:text-4xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] mb-6 transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ fontFamily: 'Georgia, serif', transitionDelay: '400ms' }}
              >
                Soluzioni su misura per{' '}
                <span className="text-[#d4883a] relative">
                  crescere
                  <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 8" fill="none">
                    <path 
                      d="M0 4C50 4 50 4 100 4C150 4 150 4 200 4" 
                      stroke="#d4883a" 
                      strokeWidth="3"
                      strokeLinecap="round"
                      className={`transition-all duration-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                      style={{ 
                        strokeDasharray: 200, 
                        strokeDashoffset: isLoaded ? 0 : 200,
                        transitionDelay: '1200ms'
                      }}
                    />
                  </svg>
                </span>
              </h1>

              {/* Subtitle */}
              <p 
                className={`text-white/60 text-base sm:text-lg lg:text-xl mb-8 sm:mb-10 leading-relaxed max-w-xl transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ fontFamily: 'Georgia, serif', transitionDelay: '600ms' }}
              >
                Non vendo pacchetti standardizzati. Ogni collaborazione è progettata 
                sulle tue <strong className="text-white">esigenze specifiche</strong>, con obiettivi chiari e risultati misurabili.
              </p>
              
              {/* Check indicators - compact row on mobile, spaced row on desktop */}
              <div 
                className={`flex items-center gap-2 mb-6 sm:hidden text-white/50 text-xs transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: '700ms' }}
              >
                <CheckCircle className="w-3 h-3 text-[#d4883a] flex-shrink-0" />
                <span>Personalizzato</span>
                <span className="text-[#d4883a]">·</span>
                <CheckCircle className="w-3 h-3 text-[#d4883a] flex-shrink-0" />
                <span>Misurabile</span>
                <span className="text-[#d4883a]">·</span>
                <CheckCircle className="w-3 h-3 text-[#d4883a] flex-shrink-0" />
                <span>Supporto diretto</span>
              </div>
              <div 
                className={`hidden sm:flex sm:flex-row sm:flex-wrap sm:gap-6 mb-8 sm:mb-10 transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: '700ms' }}
              >
                {[
                  { label: '100% personalizzato', icon: CheckCircle },
                  { label: 'Risultati misurabili', icon: CheckCircle },
                  { label: 'Supporto diretto', icon: CheckCircle },
                ].map((item, index) => (
                  <div 
                    key={index}
                    className={`flex items-center gap-2 text-white/60 text-sm sm:text-base transition-all duration-500 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                    style={{ transitionDelay: `${800 + index * 100}ms` }}
                  >
                    <item.icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#d4883a]" />
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <div 
                className={`transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: '1000ms' }}
              >
                <button
                  onClick={scrollToContact}
                  data-testid="servizi-cta-button"
                  className="group bg-[#d4883a] hover:bg-[#e09a4a] text-white font-semibold px-6 sm:px-8 py-3 sm:py-4 rounded-full transition-all flex items-center justify-center gap-2 text-base sm:text-lg hover:scale-105 hover:shadow-lg hover:shadow-[#d4883a]/25"
                >
                  Scopri come posso aiutarti
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Quote */}
        <div 
          className={`absolute bottom-8 left-6 lg:left-12 max-w-lg hidden sm:block transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          style={{ transitionDelay: '1200ms' }}
        >
          <div className="border-l-2 border-[#d4883a]/50 pl-4">
            <p className="text-white/40 italic text-sm">
              "Non cerco clienti. Cerco partner<br />
              con cui costruire successi."
            </p>
          </div>
        </div>
      </section>

      {/* Who Is This For Section */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <AnimatedSection>
              <p className="text-[#d4883a] font-semibold mb-4 tracking-widest text-sm">Per chi è</p>
              <h2 
                className="text-3xl lg:text-5xl text-[#0b114e] mb-8"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Perfetto per te <span className="text-[#d4883a]">se...</span>
              </h2>
              <div className="space-y-6">
                {idealClient.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 group">
                    <div className="w-10 h-10 min-w-[40px] min-h-[40px] bg-[#d4883a]/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#d4883a] transition-all duration-300">
                      <item.icon className="w-5 h-5 text-[#d4883a] group-hover:text-white transition-colors" />
                    </div>
                    <span className="text-gray-700 text-lg">{item.text}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={200}>
              <div className="bg-[#0b114e] rounded-3xl p-10 lg:p-12 text-center relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#d4883a]/10 rounded-full blur-2xl" />
                <div className="relative">
                  <p className="text-[#d4883a] text-7xl lg:text-8xl font-bold mb-4">89%</p>
                  <p className="text-white text-xl lg:text-2xl font-semibold mb-2">dei clienti supera gli obiettivi</p>
                  <p className="text-white/60">entro i primi 12 mesi di collaborazione</p>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 lg:py-32 bg-[#f8f9fa]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <AnimatedSection className="text-center mb-16">
            <p className="text-[#d4883a] font-semibold mb-4 tracking-widest text-sm">I servizi</p>
            <h2 
              className="text-3xl lg:text-5xl text-[#0b114e] mb-6"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Come posso <span className="text-[#d4883a]">aiutarti</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Scegli il percorso più adatto alle tue esigenze. 
              Ogni servizio può essere personalizzato in base ai tuoi obiettivi.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon] || Briefcase;
              return (
                <AnimatedSection key={service.id || index} delay={index * 150}>
                  <div 
                    className={`relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2 h-full flex flex-col ${
                      service.popular ? 'ring-2 ring-[#d4883a]' : ''
                    }`}
                  >
                    {service.popular && (
                      <div className="absolute top-4 right-4 bg-[#d4883a] text-white text-xs font-bold px-3 py-1 rounded-full">
                        Più richiesto
                      </div>
                    )}
                    
                    <div className="p-8 flex flex-col flex-grow">
                      <div className="w-14 h-14 bg-[#0b114e] rounded-xl flex items-center justify-center mb-6">
                        <Icon className="w-7 h-7 text-[#d4883a]" />
                      </div>
                      
                      <h3 className="text-xl font-bold text-[#0b114e] mb-2" style={{ fontFamily: 'Georgia, serif' }}>{service.title}</h3>
                      <p className="text-[#d4883a] text-sm font-medium mb-4">{service.subtitle}</p>
                      <p className="text-gray-600 mb-6 flex-grow">{service.description}</p>
                      
                      <div className="border-t border-gray-100 pt-6 mb-6">
                        <p className="text-2xl font-bold text-[#0b114e]">{service.price}</p>
                      </div>
                      
                      <ul className="space-y-3 mb-8">
                        {service.features?.map((feature, i) => (
                          <li key={i} className="flex items-start gap-3 text-gray-600 text-sm">
                            <CheckCircle className="w-4 h-4 text-[#d4883a] flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <button
                        onClick={scrollToContact}
                        className={`w-full py-3 rounded-full font-semibold transition-all flex items-center justify-center gap-2 mt-auto ${
                          service.popular 
                            ? 'bg-[#d4883a] hover:bg-[#e09a4a] text-white' 
                            : 'bg-[#0b114e] hover:bg-[#1a2a5e] text-white'
                        }`}
                      >
                        Richiedi informazioni
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <AnimatedSection className="text-center mb-16">
            <p className="text-[#d4883a] font-semibold mb-4 tracking-widest text-sm">Il processo</p>
            <h2 
              className="text-3xl lg:text-5xl text-[#0b114e]"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Come <span className="text-[#d4883a]">funziona</span>
            </h2>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-4 gap-8">
            {processSteps.map((item, index) => {
              const StepIcon = item.icon;
              return (
                <AnimatedSection key={index} delay={index * 100} className="text-center">
                  <div className="relative">
                    {/* Connector line */}
                    {index < processSteps.length - 1 && (
                      <div className="hidden md:block absolute top-8 left-[60%] w-full h-0.5 bg-[#d4883a]/20" />
                    )}
                    <div className="w-16 h-16 bg-[#0b114e] rounded-full flex items-center justify-center mx-auto mb-6 relative z-10">
                      <StepIcon className="w-7 h-7 text-[#d4883a]" />
                    </div>
                  </div>
                  <span className="text-[#d4883a] font-bold text-sm">{item.step}</span>
                  <h3 className="text-lg font-bold text-[#0b114e] mb-2 mt-1" style={{ fontFamily: 'Georgia, serif' }}>{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 lg:py-32 bg-[#0b114e] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#d4883a]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
        
        <div className="max-w-[900px] mx-auto px-6 lg:px-12 relative">
          <AnimatedSection className="text-center mb-16">
            <p className="text-[#d4883a] font-semibold mb-4 tracking-widest text-sm">FAQ</p>
            <h2 
              className="text-3xl lg:text-5xl text-white"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Domande <span className="text-[#d4883a]">frequenti</span>
            </h2>
          </AnimatedSection>
          
          <div>
            {faqs.map((faq, index) => (
              <AnimatedSection key={index} delay={index * 100} className="mb-6">
                <div 
                  className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden transition-all duration-300 ${openFaq === index ? 'bg-white/10' : 'hover:bg-white/10'}`}
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <h3 className="font-semibold text-white text-lg pr-4" style={{ fontFamily: 'Georgia, serif' }}>{faq.q}</h3>
                    <ChevronDown className={`w-5 h-5 text-[#d4883a] flex-shrink-0 transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96 pb-6' : 'max-h-0'}`}>
                    <p className="text-white/70 leading-relaxed px-6">{faq.a}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#f8f9fa] to-transparent" />
        <div className="absolute top-1/4 right-10 w-32 h-32 border-2 border-[#d4883a]/20 rounded-full animate-spin" style={{ animationDuration: '20s' }} />
        
        <AnimatedSection className="max-w-[800px] mx-auto px-6 lg:px-12 text-center relative">
          <h2 
            className="text-3xl lg:text-5xl text-[#0b114e] mb-6"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Pronto a fare il <span className="text-[#d4883a]">prossimo passo</span>?
          </h2>
          <p className="text-gray-600 text-lg lg:text-xl mb-10 max-w-2xl mx-auto">
            Prenota una consulenza gratuita di 30 minuti. 
            Analizzeremo insieme la tua situazione e capirai se sono la persona giusta per te.
          </p>
          <button
            onClick={scrollToContact}
            data-testid="servizi-cta-final-button"
            className="group inline-flex items-center gap-3 bg-[#d4883a] hover:bg-[#e09a4a] text-white font-semibold px-10 py-5 rounded-full transition-all text-lg hover:scale-105 hover:shadow-lg hover:shadow-[#d4883a]/25"
          >
            Prenota la tua consulenza gratuita
            <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
          </button>
        </AnimatedSection>
      </section>

      <Footer />
      <Chatbot />
    </div>
  );
};

export default ServiziPage;
